/* =========================================
   HERO SCENE — GEOMETRY
   Pure module: constants, the isometric projection, and SVG path
   builders. No React in here.

   All geometry is generated from the dimension constants below via one
   isometric projection (vertical edges stay vertical):
       X = 250 + 86.6·(x − z),   Y = 250 + 50·(x + z) − 100·y
   +x runs toward the lower-right, +z toward the lower-left, +y up.

   The belt runs along the z axis: in from the TOP-RIGHT, hidden behind
   the block, out through a slot in the front-left face heading
   BOTTOM-LEFT — then, at the turn, it folds over a hard edge and
   descends vertically down the page.

   The turn's position is NOT fixed in scene units: the component
   measures the rendered svg and solves for the turnZ that puts the
   fold exactly FOLD_LEFT_PX from the left edge of the screen, the
   same on every screen size. Everything that depends on turnZ is
   therefore built by makeTurnGeometry() instead of being a constant.
   ========================================= */

export const BG = '#fefcfa'

/* ---- Shading (light from directly above) ----
   Upward-facing surfaces stay the page colour; vertical faces fall
   into shade. Warm greys so they sit naturally on the paper bg. */
export const SHADE_SIDE = '#d8d3cb' // right-facing verticals (block + belt sides)
export const SHADE_FRONT = '#eae6df' // front-left face — catches more ambient light
export const SHADE_SLOT = '#aca69d' // inside the slot opening (darkest: a hole)

/* ---- Block dimensions (half-extents, in scene units) ---- */
const BLOCK_W = 1.9 // half-width  along x (lower-right direction)
const BLOCK_D = 2 // half-length along z (lower-left, belt direction)
const BLOCK_H = 0.5 // half-height along y

/* ---- Block position (scene units) — moves ONLY the block; the belt
   stays anchored to the page. The belt must still pass through the
   block, so keep the belt inside the block's faces:
     BLOCK_X: +down-right / -up-left   (|x| offset of belt within face)
     BLOCK_Y: +up / -down              (belt sits lower/higher on block)
     BLOCK_Z: slides the block ALONG the belt (+ toward bottom-left) */
const BLOCK_X = 0
const BLOCK_Y = -0.5
const BLOCK_Z = 0

// Block bounds derived from centre + half-extents.
const X0 = BLOCK_X - BLOCK_W
const X1 = BLOCK_X + BLOCK_W
const Y0 = BLOCK_Y - BLOCK_H
const Y1 = BLOCK_Y + BLOCK_H
const Z0 = BLOCK_Z - BLOCK_D
const Z1 = BLOCK_Z + BLOCK_D

/* ---- Belt dimensions ---- */
const BELT_W = 0.9 // half-width of the belt (along x)
const BELT_TOP = -0.55 // y of the belt's top surface (at wave midline)
const BELT_BOTTOM = -0.63 // y of the belt's underside — paper-thin slab
const BELT_T = BELT_TOP - BELT_BOTTOM // slab thickness
const BELT_FAR = 25 // how far the incoming run extends off-screen (z units)

/* ---- Undulation ----
   The belt is a thin paper ribbon: a gentle sine wave runs along its
   length (constant across its width, constant thickness top-to-
   bottom). The wave fades to flat over the last WAVE_FADE z-units
   before the fold, so the turn stays a crisp straight edge and the
   descent stays perfectly vertical.

   The wave TRAVELS: the component advances `phase` (in z-units) every
   frame, so crests flow with the belt — in from the top-right, out
   toward the bottom-left — at WAVE_SPEED z-units per second. */
const WAVE_AMP = 0.05 // wave amplitude (scene y-units; 1 unit = 100px)
export const WAVE_LEN = 3.2 // wavelength along z (scene units)
const WAVE_STEP = 0.2 // sampling step for the polyline edges
const WAVE_FADE = 2.2 // damping run-in before the fold (z units)
export const WAVE_SPEED = 0.33 // travel speed (z-units/s ≈ 33 screen px/s)

/* ---- Flat zone through the block ----
   The paper is ironed FLAT where it passes through the block: a
   bobbing crest grazing the block's silhouette edges reads as a
   glitch, so the wave dies out on the way in (fully flat before the
   entry graze at the top-back edge), stays flat through the slot,
   and picks back up shortly after the exit. The zone follows the
   block's CURRENT travel position, fed in from the scroll hook. */
const FLAT_ENTRY_END = 0.8 // fully flat this far before the back face
const FLAT_ENTRY_RAMP = 1.2 // ...ramping down over this run-in
const FLAT_EXIT_START = 0.4 // stays flat this far past the front face
const FLAT_EXIT_RAMP = 1.4 // ...then ramps back up over this run-out

const smooth = (t: number) => {
  const c = Math.min(1, Math.max(0, t))
  return c * c * (3 - 2 * c)
}

// 1 away from the block, 0 through it (the two terms never overlap).
const blockEnvelope = (z: number, travel: number) => {
  const before = smooth((Z0 + travel - FLAT_ENTRY_END - z) / FLAT_ENTRY_RAMP)
  const after = smooth((z - (Z1 + travel) - FLAT_EXIT_START) / FLAT_EXIT_RAMP)
  return Math.min(1, before + after)
}

/* ---- Entry dip ----
   The paper sits only ~half a unit below the block's top, so on its
   way in it would glide past the top-back edge with a wide background
   gap before ducking behind the silhouette. To sell the entry, the
   paper DIVES slightly as it approaches the back face — slipping
   under the block's edge with barely a gap — then levels back to
   BELT_TOP while hidden inside the block, well before the exit slot.
   Tracks the block's travel like the flat zone does. */
const ENTRY_DIP = 0.25 // dive depth (scene y-units)
const dipY = (z: number, travel: number) => {
  const back = Z0 + travel
  const down = smooth((z - (back - 1.8)) / 1.2) // dive on approach
  const release = smooth((back + 1.6 - z) / 1.0) // level out inside
  return -ENTRY_DIP * down * release
}

const waveY = (z: number, turnZ: number, phase: number, travel: number) => {
  const fade = Math.min(1, Math.max(0, (turnZ - z) / WAVE_FADE))
  const env = fade * blockEnvelope(z, travel)
  return (
    WAVE_AMP * env * Math.sin((2 * Math.PI * (z - phase)) / WAVE_LEN) +
    dipY(z, travel)
  )
}

/* ---- The run's end ----
   turnZ marks where the belt used to fold; there is no fold (or
   vertical descent) any more — the flat run simply continues past
   turnZ and exits the viewport's left edge. The wave still damps to
   flat approaching turnZ, so the paper leaves the screen calm.

   FOLD_LEFT_PX is THE knob for where turnZ sits on screen: it is
   placed exactly this many pixels from the left edge of the viewport,
   on every screen size. RUN_OVERRUN then carries the paper well past
   that point so its end is never visible. */
export const FOLD_LEFT_PX = 140
/* SSR / first-paint fallback until the component measures the svg. */
export const DEFAULT_TURN_Z = 12.84
/* How far past turnZ the outgoing run continues (z units; ~87px of
   leftward screen travel per unit — comfortably off-screen). */
const RUN_OVERRUN = 3.5

/* Where the travelling block's slide stops, measured back from turnZ.
   This used to be the standing block's thickness (the traveller
   docked against it); the block is gone but the stop stays put. */
const STAND_T = 2 * BLOCK_H

/* ---- Isometric projection ---- */
const px = (x: number, z: number) => 250 + 86.6 * (x - z)
const py = (x: number, y: number, z: number) => 250 + 50 * (x + z) - 100 * y
const pt = (x: number, y: number, z: number) =>
  `${px(x, z).toFixed(1)} ${py(x, y, z).toFixed(1)}`
// A projected point pushed straight down the screen by `d` pixels.
const ptDown = (x: number, y: number, z: number, d: number) =>
  `${px(x, z).toFixed(1)} ${(py(x, y, z) + d).toFixed(1)}`

/* Screen-pixel translation for sliding the block dz units along the
   belt axis (used by the scroll hook). */
export const travelTransform = (dz: number) =>
  `translate(${(-86.6 * dz).toFixed(2)} ${(50 * dz).toFixed(2)})`

/* ---- Faux print (see marks.tsx) ----
   The incoming paper carries rows of blurred, text-like marks. Each
   mark is placed on its lane's wavy surface line and rotated to the
   local tangent, so the "print" rides the undulation and dives into
   the block with the paper. */
export const MARK_LANES = [-0.55, -0.33, -0.11, 0.11, 0.33, 0.55]
/* Far end of the printed stretch (z units; off-screen top-right). */
export const MARK_Z_START = -16
/* Near end: the block's front face at rest. Marks past this point
   would sit UNDER the outgoing paper — invisible normally, but they'd
   ghost through it where the scene's end fade makes it translucent. */
export const MARK_Z_END = Z1

/* Open path for one mark on lane x, spanning z0..z1 ALONG the wavy
   paper surface: the mark is drawn as a thick round-capped stroke of
   this path, so it bends with the undulation instead of lying rigid
   across it. */
export function laneMarkPath(
  xLane: number,
  z0: number,
  z1: number,
  turnZ: number,
  phase: number,
  travel: number
): string {
  const n = Math.max(1, Math.ceil((z1 - z0) / WAVE_STEP))
  let d = ''
  for (let i = 0; i <= n; i++) {
    const z = z0 + ((z1 - z0) * i) / n
    const y = BELT_TOP + waveY(z, turnZ, phase, travel)
    d += `${i ? 'L' : 'M'}${px(xLane, z).toFixed(1)} ${py(xLane, y, z).toFixed(1)} `
  }
  return d.trimEnd()
}

/* ---- Wavy-ribbon edge builders ----
   An edge of the belt sampled along z from z0 to z1 (either
   direction) at fixed x, following the wave; dy drops the edge by the
   slab thickness for the underside. Returns projected points. */
const wavyEdge = (
  x: number,
  z0: number,
  z1: number,
  dy: number,
  turnZ: number,
  phase: number,
  travel: number
): string[] => {
  const n = Math.max(1, Math.ceil(Math.abs(z1 - z0) / WAVE_STEP))
  return Array.from({ length: n + 1 }, (_, i) => {
    const z = z0 + ((z1 - z0) * i) / n
    return pt(x, BELT_TOP + waveY(z, turnZ, phase, travel) - dy, z)
  })
}

// Join projected points into a closed path.
const poly = (...pts: string[]) =>
  `M${pts[0]} ${pts
    .slice(1)
    .map((p) => `L${p}`)
    .join(' ')} Z`

/* ---- Block outline (visible edges only) ---- */
// Silhouette hexagon: top-back D, right B, F, bottom E, G, left C.
export const BLOCK_OUTLINE = [
  `M${pt(X0, Y1, Z0)}`,
  `L${pt(X1, Y1, Z0)}`,
  `L${pt(X1, Y0, Z0)}`,
  `L${pt(X1, Y0, Z1)}`,
  `L${pt(X0, Y0, Z1)}`,
  `L${pt(X0, Y1, Z1)}`,
  'Z',
].join(' ')

// The two visible vertical faces, drawn shaded over the silhouette.
// Their strokes also draw the three inner edges that meet at the
// near-top vertex, so no separate edge paths are needed.
export const FACE_RIGHT = `M${pt(X1, Y1, Z0)} L${pt(X1, Y0, Z0)} L${pt(X1, Y0, Z1)} L${pt(X1, Y1, Z1)} Z`
export const FACE_FRONT = `M${pt(X0, Y1, Z1)} L${pt(X1, Y1, Z1)} L${pt(X1, Y0, Z1)} L${pt(X0, Y0, Z1)} Z`

/* ---- Slot (static) ---- */
// Slot opening on the block's front-left face (z = Z1). The paper is
// always ironed FLAT where it passes through the block (see the flat
// zone above), so the slot hugs the sheet's cross-section with just a
// thin clearance — the dark opening reads as a snug feed slot.
const SLOT_PAD = 0.03
const SLOT_TOP = BELT_TOP + SLOT_PAD
const SLOT_BOT = BELT_BOTTOM - SLOT_PAD
export const SLOT = [
  `M${pt(-BELT_W - SLOT_PAD, SLOT_TOP, Z1)}`,
  `L${pt(BELT_W + SLOT_PAD, SLOT_TOP, Z1)}`,
  `L${pt(BELT_W + SLOT_PAD, SLOT_BOT, Z1)}`,
  `L${pt(-BELT_W - SLOT_PAD, SLOT_BOT, Z1)}`,
  'Z',
].join(' ')

/* ---- Moving slot clip (static path; translated with the block) ----
   Hugs the slot — top edge of the exit opening, then straight down
   the slot's right edge — so nothing outgoing draws onto the block's
   face, and is hugely oversized below/left so it never cuts the run
   or the descent. The paper is flat at the face (the flat zone), so
   cutting exactly at the slot's corner makes the sheet fill the
   opening end-to-end, with only the thin SLOT_PAD clearance dark. */
const CLIP_MARGIN = 0.2
const CLIP_DEEP = 1800 // generous straight-down oversize (screen px)
export const OUT_CLIP = [
  `M${pt(-BELT_W - CLIP_MARGIN, SLOT_TOP, Z1)}`,
  `L${pt(BELT_W, SLOT_TOP, Z1)}`,
  `L${ptDown(BELT_W, SLOT_TOP, Z1, CLIP_DEEP)}`,
  `L${ptDown(-BELT_W - CLIP_MARGIN, SLOT_TOP, BELT_FAR, CLIP_DEEP)}`,
  `L${pt(-BELT_W - CLIP_MARGIN, SLOT_TOP, BELT_FAR)}`,
  'Z',
].join(' ')

/* ---- Solving the turn's position from the screen ----
   Given the svg's rendered bounding box, find the turnZ that puts the
   fold's front-left corner exactly `targetPx` from the viewport's
   left edge. (viewBox is 500×500, preserveAspectRatio xMidYMid meet.)
   Clamped so the fold always stays a sensible distance past the
   block's travel range, even on very narrow screens. */
export function turnZForFoldScreenX(rect: DOMRect, targetPx: number): number {
  const s = Math.min(rect.width, rect.height) / 500
  const innerLeft = rect.left + (rect.width - 500 * s) / 2
  const vbX = (targetPx - innerLeft) / s
  // Invert px(-BELT_W, turnZ) = vbX.
  const turnZ = -BELT_W - (vbX - 250) / 86.6
  return Math.min(Math.max(turnZ, Z1 + STAND_T + 1.2), 60)
}

/* ---- The wavy belt surfaces ----
   Rebuilt every animation frame with an advancing phase so the
   ripple travels along the belt (top-right toward bottom-left).
   `phase` is the wave's offset along z, in scene units; `travel` is
   the block's current slide along the belt, so the flat zone through
   the block tracks it. */
export interface BeltPaths {
  inTop: string
  inSide: string
  outTop: string
  outSide: string
}

export function makeBeltPaths(
  turnZ: number,
  phase: number,
  travel = 0
): BeltPaths {
  /* The run continues past turnZ and off the screen's left edge —
     there is no fold or descent any more. */
  const endZ = turnZ + RUN_OVERRUN
  return {
    // Top surface: right edge out to the end, left edge back — both
    // follow the wave, so the surface reads as a rippling sheet.
    inTop: poly(
      ...wavyEdge(BELT_W, -BELT_FAR, endZ, 0, turnZ, phase, travel),
      ...wavyEdge(-BELT_W, endZ, -BELT_FAR, 0, turnZ, phase, travel)
    ),
    // Side face: wavy top edge to the end, down the (off-screen)
    // edge to the underside, wavy bottom edge back.
    inSide: poly(
      ...wavyEdge(BELT_W, -BELT_FAR, endZ, 0, turnZ, phase, travel),
      ...wavyEdge(BELT_W, endZ - BELT_T, -BELT_FAR, BELT_T, turnZ, phase, travel)
    ),
    outTop: poly(
      ...wavyEdge(BELT_W, Z1, endZ, 0, turnZ, phase, travel),
      ...wavyEdge(-BELT_W, endZ, Z1, 0, turnZ, phase, travel)
    ),
    outSide: poly(
      ...wavyEdge(BELT_W, Z1, endZ, 0, turnZ, phase, travel),
      ...wavyEdge(BELT_W, endZ - BELT_T, Z1, BELT_T, turnZ, phase, travel)
    ),
  }
}

/* ---- Everything that depends on where the run's end is ---- */
export interface TurnGeometry {
  turnZ: number
  /* Scroll travel cap: the traveller stops STAND_T short of turnZ
     (where the standing block used to be). */
  maxTravel: number
  /* Page-fixed belt ribbon from far off-screen top-right out past the
     viewport's left edge, gently undulating like paper, at phase 0
     (SSR/first paint — the component then animates the phase via
     makeBeltPaths). The block and the outgoing surfaces are drawn
     over the incoming pair; the outgoing pair is redrawn IN FRONT of
     the block and cut at its face by the moving slot clip
     (OUT_CLIP). */
  inTop: string
  inSide: string
  outTop: string
  outSide: string
  /* Gradient axis (screen px) for fading the WHOLE scene out toward
     its bottom-left end: fully visible at (x1,y1), dissolved by
     (x2,y2). Runs along the belt axis, so the iso-lines of the fade
     are perpendicular to the paper's flow. */
  endFade: { x1: number; y1: number; x2: number; y2: number }
}

export function makeTurnGeometry(turnZ: number): TurnGeometry {
  const fadeStartZ = turnZ - 3.2
  const fadeEndZ = turnZ + 0.6
  return {
    turnZ,
    maxTravel: turnZ - STAND_T - Z1,
    ...makeBeltPaths(turnZ, 0),
    endFade: {
      x1: px(0, fadeStartZ),
      y1: py(0, BELT_TOP, fadeStartZ),
      x2: px(0, fadeEndZ),
      y2: py(0, BELT_TOP, fadeEndZ),
    },
  }
}
